import { useEffect, useMemo, useState } from "react";
import { NavButtons } from "../components/NavButtons";
import { Link } from "react-router-dom";
import { CalendarCheck, ArrowLeft, MagnifyingGlass, User, VideoCamera } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { getCached, setCached } from "../lib/cache";
import PageLoader from "../components/PageLoader";

const BUSINESS_TZ = "America/New_York";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const dateStr = d.toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
      timeZone: BUSINESS_TZ,
    });
    const tz = new Intl.DateTimeFormat(undefined, {
      timeZone: BUSINESS_TZ, timeZoneName: "short",
    }).formatToParts(d).find(p => p.type === "timeZoneName")?.value || "ET";
    return `${dateStr} · ${tz}`;
  } catch { return iso; }
};

function extractUrl(text) {
  if (!text) return "";
  const m = String(text).match(/https?:\/\/[^\s"<>]+/i);
  return m ? m[0] : "";
}

function shortLocation(loc) {
  if (!loc) return "";
  const s = String(loc).trim();
  // Any long free-text location — just show the first line, trimmed
  const first = s.split(/\r?\n/)[0].trim();
  return first.length > 60 ? first.slice(0, 57) + "…" : first;
}

function dayGroup(iso) {
  if (!iso) return "Unknown";
  // Compare using calendar dates in the business timezone (ET) so grouping
  // is consistent regardless of the viewer's device timezone.
  const partsFor = (date) => {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: BUSINESS_TZ, year: "numeric", month: "2-digit", day: "2-digit", weekday: "short",
    }).formatToParts(date);
    const map = {};
    for (const p of parts) map[p.type] = p.value;
    return {
      y: Number(map.year), m: Number(map.month), d: Number(map.day),
      // "Sun","Mon"..."Sat" — convert to 0..6
      dow: ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].indexOf(map.weekday),
    };
  };
  const dp = partsFor(new Date(iso));
  const tp = partsFor(new Date());
  // Days since 1970 (using the ET calendar date, no time)
  const toEpochDay = (p) => {
    // Use UTC math on the ET calendar date to avoid TZ drift
    return Math.floor(Date.UTC(p.y, p.m - 1, p.d) / 86400000);
  };
  const dEpoch = toEpochDay(dp);
  const tEpoch = toEpochDay(tp);
  const diff = dEpoch - tEpoch;
  if (diff < 0) return "Later"; // shouldn't happen — backend filters future only
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  // Days remaining until (and including) Saturday in ET
  const daysUntilSat = (6 - tp.dow + 7) % 7;
  if (diff <= daysUntilSat) return "This week";
  if (diff <= daysUntilSat + 7) return "Next week";
  return "Later";
}

export default function AdminAppointments() {
  const cached = getCached("admin-appointments");
  const [data, setData] = useState(cached ?? { available: true, appointments: [] });
  const [loading, setLoading] = useState(!cached);
  const [q, setQ] = useState("");

  const load = async () => {
    if (!cached) setLoading(true);
    try {
      const { data } = await api.get("/admin/appointments");
      setData(data);
      setCached("admin-appointments", data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return data.appointments || [];
    return (data.appointments || []).filter(a => {
      const blob = [
        a.acuity_first, a.acuity_last, a.acuity_email, a.acuity_phone,
        a.client_name, a.client_business, a.type,
      ].filter(Boolean).join(" ").toLowerCase();
      return blob.includes(term);
    });
  }, [q, data]);

  const grouped = useMemo(() => {
    const g = { "Today": [], "Tomorrow": [], "This week": [], "Next week": [], "Later": [] };
    for (const a of filtered) {
      const key = dayGroup(a.datetime);
      if (!g[key]) g[key] = [];
      g[key].push(a);
    }
    return g;
  }, [filtered]);

  return (
    <div className="min-h-screen" data-testid="admin-appointments-page"
      style={{ background: "var(--bg)", color: "var(--text)" }}>
      <header className="flex items-center justify-between gap-3 px-4 md:px-6 py-4 md:py-6 border-b" style={{ borderColor: "var(--hair)" }}>
        <div className="min-w-0">
          <NavButtons home="/admin" testid="back-to-admin" />
          <h1 className="title-xl text-2xl md:text-4xl mt-1 truncate">Appointments.</h1>
          <p className="label mt-1">Every upcoming Acuity booking, matched to a client when we can find one.</p>
        </div>
        <CalendarCheck size={28} weight="bold" />
      </header>

      <section className="p-4 md:p-6">
        {!data.available && (
          <div className="panel p-6 mb-6">
            <p className="label" style={{ color: "#991b1b", fontWeight: 700 }}>
              Acuity isn't returning appointments right now.
            </p>
            <p className="text-sm mt-1" style={{ color: "var(--text-dim)" }}>
              {data.error?.includes("403")
                ? "Acuity's API is only available on their Powerhouse plan. Upgrade at secure.acuityscheduling.com and the appointments will appear here automatically."
                : (data.error || "Add ACUITY_USER_ID and ACUITY_API_KEY in the backend .env, then reload.")}
            </p>
          </div>
        )}

        <div className="panel p-3 mb-6 flex items-center gap-2">
          <MagnifyingGlass size={16} weight="bold" />
          <input
            data-testid="appt-search"
            placeholder="Search by name, email, phone, business, or type…"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="flex-1 bg-transparent outline-none py-1" />
        </div>

        {loading ? (
          <PageLoader label="Loading appointments…" testid="admin-appointments-loader" />
        ) : filtered.length === 0 ? (
          <div className="panel p-12 text-center">
            <p className="title-xl text-2xl">Nothing on the calendar.</p>
            <p className="label mt-2">
              {q ? "No matches for that search." : "You'll see appointments here as soon as Acuity has upcoming bookings."}
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped).map(([label, list]) => (
              list.length > 0 && (
                <div key={label}>
                  <p className="label mb-3" style={{ color: "var(--text-dim)", fontWeight: 700 }}>
                    {label.toUpperCase()} · {list.length}
                  </p>
                  <div className="space-y-2">
                    {list.map(a => (
                      <ApptRow key={a.id} appt={a} />
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function meetingLabel(url) {
  const u = String(url || "").toLowerCase();
  if (u.includes("zoom.us")) return "Join Zoom";
  if (u.includes("meet.google.com")) return "Join Google Meet";
  if (u.includes("teams.microsoft.com") || u.includes("teams.live.com")) return "Join Teams";
  if (u.includes("whereby.com")) return "Join Whereby";
  if (u.includes("webex.com")) return "Join Webex";
  return "Open meeting link";
}

function ApptRow({ appt }) {
  const displayName = appt.client_name
    || `${appt.acuity_first} ${appt.acuity_last}`.trim()
    || appt.acuity_email
    || "Unknown";
  const secondary = appt.client_business
    || appt.acuity_email
    || appt.acuity_phone
    || "";
  const meetingUrl = extractUrl(appt.location);
  const isLink = !!meetingUrl;
  const locLabel = isLink ? "" : shortLocation(appt.location);
  const inner = (
    <div className="hair p-3 flex flex-col md:flex-row md:items-start md:justify-between gap-3 hover:bg-black/5 transition-colors rounded"
      data-testid={`appt-row-${appt.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium truncate">{displayName}</span>
          {!appt.client_user_id && (
            <span className="label px-2 py-0.5"
              style={{ background: "#fef3c7", color: "#78350f", border: "1px solid #fef3c7", fontWeight: 700 }}>
              Unlinked
            </span>
          )}
        </div>
        {secondary && (
          <p className="label mt-0.5 truncate" style={{ color: "var(--text-dim)" }}>
            {secondary}
          </p>
        )}
        <p className="text-xs mt-1 opacity-70 truncate">
          {appt.type}{appt.duration ? ` · ${appt.duration} min` : ""}
        </p>
        {locLabel && !isLink && (
          <p className="label mt-1 truncate" style={{ color: "var(--text-dim)" }}>
            {locLabel}
          </p>
        )}
      </div>
      <div className="flex flex-col items-start md:items-end shrink-0 gap-1 md:min-w-[200px]">
        <p className="text-sm font-semibold whitespace-nowrap">{fmtDateTime(appt.datetime)}</p>
        {isLink && (
          <a href={meetingUrl} target="_blank" rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid={`appt-join-${appt.id}`}
            className="btn-primary text-xs whitespace-nowrap"
            title={meetingUrl}>
            <VideoCamera size={12} weight="bold" /> {meetingLabel(meetingUrl)}
          </a>
        )}
        {appt.client_user_id && (
          <p className="label mt-0.5 inline-flex items-center gap-1" style={{ color: "var(--text-dim)" }}>
            <User size={10} weight="bold" /> Client profile
          </p>
        )}
      </div>
    </div>
  );
  if (appt.client_user_id) {
    return (
      <Link to={`/admin/clients/${appt.client_user_id}`} className="no-underline block" style={{ color: "inherit" }}>
        {inner}
      </Link>
    );
  }
  return inner;
}
